/**
 * Admin → Patient eCards (Library + Requests) panel.
 * Two sub-tabs:
 *   - Library: bulk-upload ZIP of card PNGs, browse, delete
 *   - Requests: prioritize practice-submitted eCard design requests
 */
import React, { useCallback, useEffect, useState } from 'react';
import { CheckCircle2, PlayCircle, XCircle, Trash2, RefreshCw, Loader2, Mail, Phone, Building2, Clock, Upload, Image as ImageIcon } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

function fmt(d) {
  if (!d) return '—';
  try { return new Date(d).toLocaleString(); } catch { return d; }
}

const STATUSES = [
  { id: 'open',        label: 'Open',        tone: 'amber',   Icon: Clock },
  { id: 'in_progress', label: 'In Progress', tone: 'blue',    Icon: PlayCircle },
  { id: 'done',        label: 'Done',        tone: 'emerald', Icon: CheckCircle2 },
  { id: 'declined',    label: 'Declined',    tone: 'rose',    Icon: XCircle },
];

const TONE = {
  amber:   'bg-amber-100 text-amber-800 border-amber-300',
  blue:    'bg-blue-100 text-blue-800 border-blue-300',
  emerald: 'bg-emerald-100 text-emerald-800 border-emerald-300',
  rose:    'bg-rose-100 text-rose-800 border-rose-300',
};

const LIBRARY_CATEGORIES = ['Restorative', 'Cosmetic', 'Hygiene', 'Emergency', 'Implants', 'Kids', 'General'];

// ---------------------------------------------------------------------------
// Library sub-panel
// ---------------------------------------------------------------------------

function LibraryPanel({ adminToken }) {
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/admin/ecards`, {
        headers: { Authorization: `Bearer ${adminToken}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      setCards(json.cards || []);
    } catch (e) {
      console.error('load ecards failed', e);
    } finally {
      setLoading(false);
    }
  }, [adminToken]);

  useEffect(() => { load(); }, [load]);

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.zip')) {
      alert('Please choose a .zip file'); return;
    }
    setUploading(true); setUploadResult(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      // Pass token in BOTH header and query param so the request survives
      // proxies/CDNs that strip Authorization on multipart POSTs.
      const url = `${API}/api/admin/ecards/upload-zip?token=${encodeURIComponent(adminToken || '')}`;
      const res = await fetch(url, {
        method: 'POST',
        headers: { Authorization: `Bearer ${adminToken}` },
        body: fd,
      });
      let json = {};
      try { json = await res.json(); } catch {}
      if (!res.ok) {
        if (res.status === 401) {
          throw new Error(`${json.detail || 'Your admin session expired'} — please log out and sign back in, then retry the upload.`);
        }
        throw new Error(json.detail || `HTTP ${res.status}`);
      }

      // Server now processes in background and returns a job_id. Poll it.
      const jobId = json.job_id;
      if (!jobId) {
        // Legacy sync response — treat as done
        setUploadResult(json);
        await load();
        return;
      }
      setUploadResult({ ...json, status: 'running', processed: 0, total: json.total || 0 });

      const pollUrl = `${API}/api/admin/ecards/upload-zip/${jobId}?token=${encodeURIComponent(adminToken || '')}`;
      // Poll every 1.5s up to 20 minutes
      const deadline = Date.now() + 20 * 60 * 1000;
      while (Date.now() < deadline) {
        await new Promise(r => setTimeout(r, 1500));
        let s;
        try {
          const r = await fetch(pollUrl, { headers: { Authorization: `Bearer ${adminToken}` } });
          s = await r.json();
        } catch { continue; }
        setUploadResult(prev => ({ ...prev, ...s }));
        if (s.status === 'done') { await load(); break; }
        if (s.status === 'error') {
          alert(`Upload failed during processing: ${s.error || 'unknown error'}`);
          break;
        }
      }
    } catch (e) {
      alert(`Upload failed: ${e.message}`);
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const removeCard = async (card) => {
    if (!window.confirm(`Delete "${card.title}"? This cannot be undone.`)) return;
    setBusyId(card.id);
    try {
      const res = await fetch(`${API}/api/admin/ecards/${card.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${adminToken}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      await load();
    } catch (e) {
      alert(`Delete failed: ${e.message}`);
    } finally {
      setBusyId(null);
    }
  };

  const changeCategory = async (card, newCategory) => {
    if (newCategory === card.category) return;
    setBusyId(card.id);
    // optimistic update
    setCards(cs => cs.map(c => c.id === card.id ? { ...c, category: newCategory } : c));
    try {
      const res = await fetch(`${API}/api/admin/ecards/${card.id}`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${adminToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ category: newCategory }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
    } catch (e) {
      alert(`Failed to update category: ${e.message}`);
      await load(); // revert
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="space-y-4">
      {/* Upload zone */}
      <div className="bg-gradient-to-r from-sky-50 to-blue-50 border-2 border-dashed border-sky-300 rounded-xl p-6">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div className="min-w-0">
            <h3 className="text-base font-semibold text-slate-900 flex items-center gap-2">
              <Upload className="h-4 w-4 text-sky-600" /> Bulk Upload eCards
            </h3>
            <p className="text-sm text-slate-600 mt-1">
              Upload a <code className="text-xs bg-white/70 px-1 rounded">.zip</code> of card PNGs. Titles are pulled from filenames; categories are auto-guessed.
            </p>
          </div>
          <label className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-white text-sm font-medium cursor-pointer
                            ${uploading ? 'bg-sky-400 cursor-wait' : 'bg-sky-600 hover:bg-sky-700'}`}>
            {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
            {uploading ? 'Uploading…' : 'Choose ZIP file'}
            <input
              type="file"
              accept=".zip,application/zip"
              className="hidden"
              onChange={onUpload}
              disabled={uploading}
              data-testid="ecards-library-upload-input"
            />
          </label>
        </div>
        {uploadResult && (
          <div
            className={`mt-3 p-3 rounded-lg border text-sm ${
              uploadResult.status === 'error'
                ? 'bg-rose-50 border-rose-200'
                : uploadResult.status === 'done'
                ? 'bg-emerald-50 border-emerald-200'
                : 'bg-blue-50 border-blue-200'
            }`}
            data-testid="ecards-upload-result"
          >
            {uploadResult.status === 'running' && (
              <>
                <div className="font-semibold text-blue-700 flex items-center gap-2">
                  <RefreshCw className="h-4 w-4 animate-spin" />
                  Processing… {uploadResult.processed || 0} / {uploadResult.total || '?'}
                </div>
                {uploadResult.total > 0 && (
                  <div className="mt-2 h-2 bg-blue-100 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-blue-500 transition-all"
                      style={{ width: `${Math.round(((uploadResult.processed || 0) / uploadResult.total) * 100)}%` }}
                    />
                  </div>
                )}
                <div className="mt-1 text-xs text-slate-600">
                  You can stay on this page — upload continues in the background.
                </div>
              </>
            )}
            {uploadResult.status === 'done' && (
              <>
                <div className="font-semibold text-emerald-700">
                  ✅ Added {uploadResult.added_count} · Skipped {uploadResult.skipped_count}
                </div>
                {uploadResult.skipped?.length > 0 && (
                  <div className="mt-2 text-xs text-slate-500 max-h-24 overflow-y-auto">
                    Skipped: {uploadResult.skipped.join(', ')}
                  </div>
                )}
              </>
            )}
            {uploadResult.status === 'error' && (
              <div className="font-semibold text-rose-700">
                ❌ {uploadResult.error || 'Upload failed'}
              </div>
            )}
            {!uploadResult.status && (
              <div className="font-semibold text-emerald-700">
                ✅ Added {uploadResult.added_count} · Skipped {uploadResult.skipped_count}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Card list */}
      <div className="bg-white rounded-xl border border-slate-200">
        <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-slate-900">Library ({cards.length})</h3>
          <button
            onClick={load}
            className="inline-flex items-center gap-2 px-3 py-1 rounded-md border border-slate-300 hover:bg-slate-50 text-xs"
          >
            <RefreshCw className="h-3 w-3" /> Refresh
          </button>
        </div>
        {loading ? (
          <div className="flex items-center justify-center py-12 text-slate-500">
            <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading…
          </div>
        ) : cards.length === 0 ? (
          <div className="p-10 text-center text-slate-500">
            <ImageIcon className="h-8 w-8 mx-auto text-slate-300 mb-2" />
            <p className="text-sm">No cards yet — upload a ZIP to get started.</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 p-4">
            {cards.map(c => (
              <div key={c.id} className="group relative bg-slate-50 rounded-lg overflow-hidden border border-slate-200 hover:shadow-md transition-shadow">
                <div className="aspect-[3/4] bg-slate-100 overflow-hidden flex items-center justify-center">
                  <img
                    src={`${API}/api/ecards/${c.id}/thumbnail`}
                    alt={c.title}
                    className="w-full h-full object-contain"
                    loading="lazy"
                  />
                </div>
                <div className="p-2 space-y-1">
                  <select
                    value={c.category}
                    disabled={busyId === c.id}
                    onChange={(e) => changeCategory(c, e.target.value)}
                    className="w-full text-[10px] font-semibold text-blue-700 uppercase tracking-wide bg-blue-50 border border-blue-200 rounded px-1.5 py-0.5 cursor-pointer hover:bg-blue-100 focus:outline-none focus:ring-1 focus:ring-blue-400"
                    data-testid={`ecards-library-category-${c.id}`}
                    title="Change category"
                  >
                    {LIBRARY_CATEGORIES.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                  <div className="text-xs font-semibold text-slate-800 leading-snug line-clamp-2">{c.title}</div>
                </div>
                <button
                  onClick={() => removeCard(c)}
                  disabled={busyId === c.id}
                  className="absolute top-2 right-2 p-1.5 bg-white/90 hover:bg-rose-50 text-rose-600 rounded-md opacity-0 group-hover:opacity-100 transition-opacity shadow-sm"
                  data-testid={`ecards-library-delete-${c.id}`}
                  title="Delete card"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}


// ---------------------------------------------------------------------------
// Requests sub-panel (existing logic, extracted)
// ---------------------------------------------------------------------------

function RequestsPanel({ adminToken }) {
  const [filter, setFilter] = useState('open');
  const [data, setData] = useState({ counts: {}, requests: [] });
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [noteDraft, setNoteDraft] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const url = `${API}/api/admin/ecards/requests${filter === 'all' ? '' : `?status=${filter}`}`;
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${adminToken}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      setData(json);
    } catch (e) {
      console.error('load ecard requests failed', e);
    } finally {
      setLoading(false);
    }
  }, [adminToken, filter]);

  useEffect(() => { load(); }, [load]);

  const setStatus = async (req, status) => {
    setBusyId(req.id);
    try {
      const notes = noteDraft[req.id] !== undefined ? noteDraft[req.id] : req.admin_notes;
      const res = await fetch(`${API}/api/admin/ecards/requests/${req.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${adminToken}` },
        body: JSON.stringify({ status, admin_notes: notes }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      await load();
    } catch (e) {
      alert(`Update failed: ${e.message}`);
    } finally {
      setBusyId(null);
    }
  };

  const saveNotes = async (req) => {
    setBusyId(req.id);
    try {
      const res = await fetch(`${API}/api/admin/ecards/requests/${req.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${adminToken}` },
        body: JSON.stringify({ status: req.status, admin_notes: noteDraft[req.id] || '' }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      await load();
      setNoteDraft(nd => { const c = { ...nd }; delete c[req.id]; return c; });
    } catch (e) {
      alert(`Save failed: ${e.message}`);
    } finally {
      setBusyId(null);
    }
  };

  const removeReq = async (req) => {
    if (!window.confirm(`Delete request "${req.topic}"? This cannot be undone.`)) return;
    setBusyId(req.id);
    try {
      const res = await fetch(`${API}/api/admin/ecards/requests/${req.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${adminToken}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      await load();
    } catch (e) {
      alert(`Delete failed: ${e.message}`);
    } finally {
      setBusyId(null);
    }
  };

  const requests = data.requests || [];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h3 className="text-base font-semibold text-slate-900">Practice-Submitted Requests</h3>
          <p className="text-sm text-slate-600">Prioritize the next eCards to design based on demand.</p>
        </div>
        <button
          onClick={load}
          className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border border-slate-300 hover:bg-slate-50 text-sm"
          data-testid="ecard-requests-refresh"
        >
          <RefreshCw className="h-4 w-4" /> Refresh
        </button>
      </div>

      <div className="flex flex-wrap gap-2">
        {STATUSES.map(s => {
          const count = data.counts?.[s.id] ?? 0;
          const active = filter === s.id;
          return (
            <button
              key={s.id}
              onClick={() => setFilter(s.id)}
              data-testid={`ecard-requests-filter-${s.id}`}
              className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${
                active ? TONE[s.tone] : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50'
              }`}
            >
              {s.label} <span className="ml-1 text-xs opacity-80">({count})</span>
            </button>
          );
        })}
        <button
          onClick={() => setFilter('all')}
          className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${
            filter === 'all' ? 'bg-slate-800 text-white border-slate-800' : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50'
          }`}
          data-testid="ecard-requests-filter-all"
        >
          All
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16 text-slate-500">
          <Loader2 className="h-6 w-6 animate-spin mr-2" /> Loading requests…
        </div>
      ) : requests.length === 0 ? (
        <div className="text-center py-16 text-slate-500 bg-white rounded-xl border border-dashed">
          <p>No {filter === 'all' ? '' : filter.replace('_', ' ')} requests.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {requests.map(req => {
            const status = STATUSES.find(s => s.id === req.status) || STATUSES[0];
            const StatusIcon = status.Icon;
            const draftNote = noteDraft[req.id];
            const notesChanged = draftNote !== undefined && draftNote !== (req.admin_notes || '');
            return (
              <div
                key={req.id}
                data-testid={`ecard-request-row-${req.id}`}
                className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm"
              >
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold border ${TONE[status.tone]}`}>
                        <StatusIcon className="h-3 w-3" /> {status.label}
                      </span>
                      <span className="text-xs text-slate-500">{fmt(req.created_at)}</span>
                    </div>
                    <div className="text-lg font-semibold text-slate-900 mt-1.5">{req.topic}</div>
                  </div>
                  <button
                    onClick={() => removeReq(req)}
                    disabled={busyId === req.id}
                    className="p-2 text-rose-600 hover:bg-rose-50 rounded-lg"
                    data-testid={`ecard-request-delete-${req.id}`}
                    title="Delete"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>

                <div className="grid md:grid-cols-3 gap-2 mt-3 text-sm text-slate-700">
                  <div className="flex items-center gap-2 min-w-0">
                    <Building2 className="h-4 w-4 text-slate-400 shrink-0" />
                    <span className="truncate">{req.practice_name || '—'}</span>
                  </div>
                  <div className="flex items-center gap-2 min-w-0">
                    <Mail className="h-4 w-4 text-slate-400 shrink-0" />
                    {req.requester_email
                      ? <a href={`mailto:${req.requester_email}`} className="text-blue-600 hover:underline truncate">{req.requester_email}</a>
                      : <span>—</span>}
                  </div>
                  <div className="flex items-center gap-2 min-w-0">
                    <Phone className="h-4 w-4 text-slate-400 shrink-0" />
                    <span className="truncate">{req.practice_phone || '—'}</span>
                  </div>
                </div>

                {req.details && (
                  <div className="mt-3 bg-slate-50 border border-slate-200 rounded-lg p-3 text-sm text-slate-700 whitespace-pre-wrap">
                    {req.details}
                  </div>
                )}

                <div className="mt-3">
                  <label className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Admin Notes</label>
                  <textarea
                    rows={2}
                    value={draftNote !== undefined ? draftNote : (req.admin_notes || '')}
                    onChange={(e) => setNoteDraft(nd => ({ ...nd, [req.id]: e.target.value }))}
                    className="w-full mt-1 border border-slate-300 rounded-lg px-3 py-2 text-sm"
                    placeholder="Internal notes, ETA, blockers…"
                    data-testid={`ecard-request-notes-${req.id}`}
                  />
                  {notesChanged && (
                    <button
                      onClick={() => saveNotes(req)}
                      disabled={busyId === req.id}
                      className="mt-1 text-xs px-3 py-1 bg-slate-800 text-white rounded-md hover:bg-slate-900"
                    >
                      Save notes
                    </button>
                  )}
                </div>

                <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-slate-100">
                  {STATUSES.filter(s => s.id !== req.status).map(s => {
                    const Icon = s.Icon;
                    return (
                      <button
                        key={s.id}
                        onClick={() => setStatus(req, s.id)}
                        disabled={busyId === req.id}
                        data-testid={`ecard-request-set-${s.id}-${req.id}`}
                        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors hover:bg-slate-50 ${
                          s.id === 'done' ? 'text-emerald-700 border-emerald-300' :
                          s.id === 'in_progress' ? 'text-blue-700 border-blue-300' :
                          s.id === 'declined' ? 'text-rose-700 border-rose-300' :
                          'text-amber-700 border-amber-300'
                        }`}
                      >
                        <Icon className="h-4 w-4" /> Mark {s.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}


// ---------------------------------------------------------------------------
// Combined panel with sub-tabs
// ---------------------------------------------------------------------------

export default function EcardRequestsAdminPanel({ adminToken }) {
  const [sub, setSub] = useState('library');

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-xl font-bold text-slate-900">Patient Education Studio</h2>
        <p className="text-sm text-slate-600">Manage the library and prioritize design requests.</p>
      </div>

      <div className="flex gap-1 border-b border-slate-200">
        <button
          onClick={() => setSub('library')}
          data-testid="ecards-admin-tab-library"
          className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px transition-colors ${
            sub === 'library'
              ? 'border-blue-600 text-blue-700'
              : 'border-transparent text-slate-600 hover:text-slate-900'
          }`}
        >
          Library
        </button>
        <button
          onClick={() => setSub('requests')}
          data-testid="ecards-admin-tab-requests"
          className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px transition-colors ${
            sub === 'requests'
              ? 'border-blue-600 text-blue-700'
              : 'border-transparent text-slate-600 hover:text-slate-900'
          }`}
        >
          Requests
        </button>
      </div>

      {sub === 'library' ? (
        <LibraryPanel adminToken={adminToken} />
      ) : (
        <RequestsPanel adminToken={adminToken} />
      )}
    </div>
  );
}
